import { ILockedRecord, LockedRecord } from "ui/Scripts/source/models/common/lockedRecord";
import { DatabaseRecordType } from "ui/Scripts/source/models/enums";
import { Base } from "ui/Scripts/source/framework/base";
import { AppUtils } from "../../framework/appUtils";
import { IndexedDbManager } from "../indexedDb/indexedDbManager";
import { postApiCall } from "./tokenService";

export interface IUnlockResultItem {
    message: string;
    id: string;
    lockedDate: number;
    lockedUserCode: string;
}

export class UnlockResultItem implements IUnlockResultItem {
    message: string;
    id: string;
    lockedDate: number;
    lockedUserCode: string;

    constructor();
    constructor(obj: IUnlockResultItem);
    constructor(obj?: any) {
        this.message = obj && obj.message || "";
        this.id = obj && obj.id || "";
        this.lockedDate = obj && obj.lockedDate || 0;
        this.lockedUserCode = obj && obj.lockedUserCode || "";
    }
}

export interface IUnlockResult {
    items: IUnlockResultItem[];
}

export class UnlockResult implements IUnlockResult {
    items: UnlockResultItem[];

    constructor();
    constructor(obj: IUnlockResult);
    constructor(obj?: any) {
        this.items = Base.getTypedArray(obj ? obj.items : null, UnlockResultItem);
    }
}

type TRecordUnlockCallBack = (id: string) => void;
type TRecordLockCallBack = (id: string) => void;

const unlock = (lockedRecords: ILockedRecord[], lockToCurrentUser: boolean): Promise<UnlockResult> => {
    return postApiCall("api/locking/unlock", UnlockResult, JSON.stringify({
        records: lockedRecords.map(i => {
            return { companyId: i.companyId, id: i.id, recordType: i.recordType };
        }),
        lockToCurrentUser
    }), null, true);
};

class RecordLockManager {
    private unlockCallBacks: TRecordUnlockCallBack[] = [];
    private lockCallBacks: TRecordLockCallBack[] = [];

    addUnlockCallBack(unlockCallBack: TRecordUnlockCallBack) {
        if (!unlockCallBack) return;
        this.unlockCallBacks.push(unlockCallBack);
    }

    removeUnlockCallBack(unlockCallBack: TRecordUnlockCallBack) {
        if (!unlockCallBack) return;
        this.unlockCallBacks = this.unlockCallBacks.filter(i => !unlockCallBack);
    }

    addLockCallBack(lockCallBack: TRecordLockCallBack) {
        if (!lockCallBack) return;
        this.lockCallBacks.push(lockCallBack);
    }

    removeLockCallBack(lockCallBack: TRecordLockCallBack) {
        if (!lockCallBack) return;
        this.lockCallBacks = this.lockCallBacks.filter(i => !lockCallBack);
    }

    addLock(lockedRecord: ILockedRecord) {
        if (!lockedRecord) return;
        IndexedDbManager.addLockedRecords([lockedRecord]);
    }

    async unlockDb(lockedRecords: ILockedRecord[], lockToCurrentUser: boolean): Promise<UnlockResult> {
        if (!lockedRecords || lockedRecords.length < 1) return null;
        const result = await AppUtils.callService(() => unlock(lockedRecords, lockToCurrentUser), false, true);
        if (!result) return null;
        const successItems = result.items.filter(i => !i.message);
        if (lockToCurrentUser) {
            for (const lockCallBack of this.lockCallBacks) {
                for (const successItem of successItems) {
                    lockCallBack(successItem.id);
                }
            }
        } else {
            for (const unlockCallBack of this.unlockCallBacks) {
                for (const successItem of successItems) {
                    unlockCallBack(successItem.id);
                }
            }
        }
        await IndexedDbManager.removeLockedRecords(result.items.map(i => i.id));
        if (lockToCurrentUser) {
            const newRecords: ILockedRecord[] = [];
            for (const resultItem of successItems) {
                const lockedRecord = lockedRecords.find(j => j.id === resultItem.id);
                if (!lockedRecord) continue;
                newRecords.push(LockedRecord.createLockedRecord(lockedRecord.companyId, lockedRecord.id, resultItem.lockedUserCode, lockedRecord.recordType));
            }
            IndexedDbManager.addLockedRecords(newRecords);
        }
        return result;
    }

    async unlock(companyId: string, id: string, recordType: DatabaseRecordType): Promise<UnlockResult> {
        if (!companyId || !id) return null;
        return await this.unlockDb([LockedRecord.createLockedRecord(companyId, id, "", recordType)], false);
    }

    async unlockAll(companyId: string, userCode: string, ignoreRecordType?: DatabaseRecordType): Promise<UnlockResult> {
        if (!companyId || !userCode) return null;
        let lockedRecords = (await IndexedDbManager.getLockedRecords()).filter(i => i.userCode === userCode);
        if (ignoreRecordType) {
            lockedRecords = lockedRecords.filter(i => i.recordType !== ignoreRecordType);
        }
        return await this.unlockDb(lockedRecords, false);
    }

    async lock(companyId: string, id: string, userCode: string, recordType: DatabaseRecordType): Promise<UnlockResult> {
        if (!companyId || !id) return;
        return await this.unlockDb([LockedRecord.createLockedRecord(companyId, id, userCode, recordType)], true);
    }
}

export const recordLockManager = new RecordLockManager();
